// src/views/home/hooks/useHomeLogic.ts

import { useEffect, useState, FormEvent, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../core/redux/store";
import { fetchAllCategories } from "../../../core/redux/categorySlice";
import { getUserProfile } from "../../../core/redux/userSlice";
import { statsService } from "../../../core/services/stats.service";
import { utils } from "../../../core/helpers";
import {
    ContentAttempt, ContentGroup, ContentStatus, SelectGridItem, ValidationErrors
} from "../types";

export const useHomeLogic = () => {
    const dispatch: AppDispatch = useDispatch();
    const navigate = useNavigate();
    const { categories, loading: categoriesLoading } = useSelector(
        (state: RootState) => state.categories
    );
    const { levels } = useSelector((state: RootState) => state.levels);
    const { user } = useSelector((state: RootState) => state.user);

    const [latestContents, setLatestContents] = useState<ContentAttempt[]>([]);
    const [isLatestContentsFetched, setIsLatestContentsFetched] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<string | number | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>({
        level: "",
        category: "",
    });
    const [selectedStatus, setSelectedStatus] = useState<ContentStatus>("All");

    if (!utils.getStore("selectedCohort") || utils.getStore("selectedCohort") === "undefined" || utils.getStore("selectedCohort") === "null"){
        utils.setStore("selectedCohort", user && user.classrooms && user.classrooms[0])
    }
    const classLevels: string[] = user && user.classrooms && user.classrooms.length > 0
        ? JSON.parse(utils.getStore("selectedCohort")!).levels.map((x: any) => x.id.toString())
        : []
    const isLevelRestricted = classLevels.length > 0;


    const getContentStatus = useCallback((record: ContentAttempt): ContentStatus => {
        if (!record.is_solved) return "Opened";
        const score = record.score ? parseFloat(record.score) : 0;
        const maxScore = record.max_score ? parseFloat(record.max_score) : 0;
        if (maxScore > 0 && (score / maxScore) > 0.7) return "Success";
        return "In Progress";
    }, []);

    useEffect(() => {
        if (!user) {
            dispatch(getUserProfile());
        }

        if (utils.getStore("selectedLevel") && utils.getStore("selectedCategory")) {
            navigate("/contents");
            return;
        }
    }, [user, navigate, dispatch]);

    useEffect(() => {
        if (categories.length === 0 && !categoriesLoading) {
            dispatch(fetchAllCategories());
        }
    }, [categories, dispatch, categoriesLoading]);

    useEffect(() => {
        if (latestContents.length === 0 && !isLatestContentsFetched) {
            statsService.getLatestContents().then((data) => {
                const sortedData = data.sort((a: ContentAttempt, b: ContentAttempt) =>
                    new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime()
                );
                setLatestContents(sortedData.slice(0, 20));
                setIsLatestContentsFetched(true);
            });
        }
    }, [latestContents, isLatestContentsFetched]);


    useEffect(() => {
        if (selectedLevel && !isLevelRestricted) {
            const categorySection = document.getElementById("category-section");
            if (categorySection) {
                const header = document.querySelector("header");
                const headerHeight = header ? header.getBoundingClientRect().height : 0;
                const sectionTop =
                    categorySection.getBoundingClientRect().top + window.pageYOffset;
                window.scrollTo({
                    top: sectionTop - headerHeight - 16,
                    behavior: "smooth",
                });
            }
        }
    }, [selectedLevel, isLevelRestricted]);

    const handleLevelSelect = useCallback((data: SelectGridItem[]): void => {
        const selectedItem = data.find((item) => item.selected);
        setSelectedLevel(selectedItem ? String(selectedItem.value) : null);
        if (selectedItem) {
            setErrors((prevErrors) => ({ ...prevErrors, level: "" }));
        }
    }, []);

    const handleCategorySelect = useCallback((data: SelectGridItem[]): void => {
        const selectedItem = data.find((item) => item.selected);
        setSelectedCategory(selectedItem ? selectedItem.value : null);
        if (selectedItem) {
            setErrors((prevErrors) => ({ ...prevErrors, category: "" }));
        }
    }, []);

    const handleStatusSelect = useCallback((status: ContentStatus): void => {
        setSelectedStatus(status);
    }, []);

    const handleSubmit = useCallback((event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        const validationErrors: ValidationErrors = { level: "", category: "" };
        let isValid = true;
        let levelToStore: string[] = [];
        let levelValueForValidation: string | string[] | null = null;


        if (isLevelRestricted) {
            levelToStore = classLevels;
            levelValueForValidation = classLevels;
        } else {
            if (selectedLevel) {
                levelToStore = [selectedLevel];
                levelValueForValidation = selectedLevel;
            } else {
                validationErrors.level = "Please select a level.";
                isValid = false;
            }
        }

        if (!levelValueForValidation || (Array.isArray(levelValueForValidation) && levelValueForValidation.length === 0)) {
            validationErrors.level = "Levels could not be determined.";
            isValid = false;
        }

        if (!selectedCategory) {
            validationErrors.category = "Please select a skill.";
            isValid = false;
        }

        setErrors(validationErrors);

        if (isValid) {
            //@ts-ignore
            const levelStringForStore = JSON.stringify([...new Set(levelToStore)]);
            utils.setStore("selectedLevel", levelStringForStore);
            utils.setStore("selectedCategory", selectedCategory);
            navigate("/contents");
        }
    }, [isLevelRestricted, classLevels, selectedLevel, selectedCategory, navigate]);

    const levelData: SelectGridItem[] = useMemo(() => levels.map((level: any) => ({
        name: level.name,
        value: level.value,
        id: level.value,
        icon: level.icon,
        description: level.description,
    })), [levels]);

    const categoryData: SelectGridItem[] = useMemo(() => categories.map((x: any) => ({
        name: x.name,
        value: x.id,
        id: x.id,
        icon: x.icon,
        description: x.description,
    })), [categories]);

    const groupedContents = useMemo((): ContentGroup[] => {
        const groups = new Map<string, ContentGroup>();
        for (const attempt of latestContents) {
            const groupKey = `${attempt.content_title}-${attempt.level_name}-${attempt.category_name}`;
            const existingGroup = groups.get(groupKey);
            if (!existingGroup) {
                groups.set(groupKey, {
                    key: groupKey,
                    content_title: attempt.content_title,
                    level_name: attempt.level_name,
                    category_name: attempt.category_name,
                    mostRecentAttempt: attempt,
                    allAttempts: [attempt],
                });
            } else {
                existingGroup.allAttempts.push(attempt);
                existingGroup.allAttempts.sort((a, b) => new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime());
                existingGroup.mostRecentAttempt = existingGroup.allAttempts[0];
            }
        }
        return Array.from(groups.values()).sort((a, b) =>
            new Date(b.mostRecentAttempt.opened_at).getTime() - new Date(a.mostRecentAttempt.opened_at).getTime()
        );
    }, [latestContents]);

    const filteredGroupedContents = useMemo(() => {
        if (selectedStatus === "All") {
            return groupedContents;
        }
        return groupedContents.filter(
            (group) => getContentStatus(group.mostRecentAttempt) === selectedStatus
        );
    }, [groupedContents, selectedStatus, getContentStatus]);

    const isLoading = categoriesLoading || !user;

    return {
        user,
        errors,
        isLoading,
        levelData,
        categoryData,
        groupedContents,
        selectedStatus,
        latestContents,
        isLevelRestricted,
        filteredGroupedContents,
        selectedLevel,
        selectedCategory,

        // Handlers
        handleSubmit,
        handleLevelSelect,
        handleStatusSelect,
        getContentStatus,
        handleCategorySelect,
    };
};